import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { useTransferStore } from "@/stores/transfer-store";
import { usePermission } from "@/hooks/use-permission";
import type { ApprovalDetail, ApprovalStatus } from "@/types";

export type ApprovalFlowStep = "idle" | "confirm" | "mfa" | "approved";

/* Mirrors transfer-detail.tsx's WalletRefText — only "Name (WLT-NNN)" style
   internal wallet references get a real link; external addresses stay plain. */
function WalletRefText({ text }: { text: string }) {
  const match = text.match(/\(WLT-\d+\)/);
  if (!match) return <>{text}</>;
  const walletId = match[0].slice(1, -1);
  return (
    <Link to={`/wallets/${walletId}`} className="font-semibold text-primary hover:underline">
      {text}
    </Link>
  );
}

const DIALOG_CARD =
  "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-[16px] bg-surface p-8 shadow-[0_24px_48px_rgba(16,24,32,0.18)] outline-none";

function DialogShell({ onClose, label, children }: {
  onClose?: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Root defaultOpen onOpenChange={(open) => !open && onClose?.()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content className={DIALOG_CARD}>
          <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {label} — review and confirm, or cancel.
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ConfirmApprovalDialog({ detail, onConfirm, onClose }: {
  detail: ApprovalDetail;
  onConfirm: () => void;
  onClose?: () => void;
}) {
  const q = detail.quorumLabel.match(/Required:\s*(\d+)\s*of\s*(\d+)/);
  const required = q ? `${q[1]} of ${q[2]} required` : "";
  return (
    <DialogShell onClose={onClose} label={`Approve Transfer ${detail.transferId}?`}>
      <div className="flex flex-col items-center text-center">
        <span aria-hidden className="text-[32px] leading-none">✅</span>
        <h2 className="mt-3 text-[19px] font-bold text-ink">
          Approve Transfer {detail.transferId}?
        </h2>
        <p className="mt-2 max-w-[396px] text-[12.5px] leading-relaxed text-muted">
          You are providing the final approval ({required}). Once approved, this transfer will proceed to signing and
          broadcast immediately.
        </p>
        <div className="mt-5 w-full rounded-lg bg-bg px-4 py-3">
          {[
            { k: "Amount", v: detail.amount, strong: true },
            { k: "From", v: detail.source },
            { k: "To", v: detail.destination },
          ].map((row) => (
            <div key={row.k} className="flex items-center justify-between gap-4 border-b border-line/60 py-1 last:border-b-0">
              <span className="text-[12px] text-muted">{row.k}</span>
              <span className={cn("text-right text-[12.5px] text-ink", row.strong ? "font-bold" : "font-medium")}>
                {row.v}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-5 flex w-full items-center justify-center gap-2">
          <DialogPrimitive.Close className="inline-flex" asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogPrimitive.Close>
          <Button variant="primary" onClick={onConfirm}>
            Confirm Approval
          </Button>
        </div>
        <p className="mt-5 text-[10.5px] leading-relaxed text-subtle">
          An evidence receipt will be generated. This action is logged in the admin audit trail.
        </p>
      </div>
    </DialogShell>
  );
}

function StepUpMfaDialog({ detail, onConfirm, onClose }: {
  detail: ApprovalDetail;
  onConfirm: () => void;
  onClose?: () => void;
}) {
  const riskVariant = detail.risk === "high" ? "warning" : "success";
  const riskLabel = detail.risk === "high" ? "High Value" : "Low Risk";
  return (
    <DialogShell onClose={onClose} label={`Step-up authentication — ${detail.transferId}`}>
      <div className="flex flex-col">
        <div className="flex items-start gap-2 rounded-lg bg-info-soft p-3 text-left">
          <span aria-hidden className="text-[14px] leading-none">🔐</span>
          <span className="text-[12px] leading-relaxed text-info">Step-up authentication required for this sensitive operation</span>
        </div>
        <dl className="mt-4 w-full overflow-hidden rounded-lg border border-line">
          {[
            { k: "Operation", v: `Approve transfer ${detail.transferId}` },
            { k: "Amount", v: detail.amount },
            { k: "From", v: detail.source },
            {
              k: "Risk level",
              node: (
                <Badge variant={riskVariant} dot={false} size="sm">
                  {riskLabel}
                </Badge>
              ),
            },
          ].map((row) => (
            <div key={row.k} className="flex items-center justify-between gap-4 border-b border-line px-4 py-2.5 last:border-b-0">
              <dt className="shrink-0 text-[12px] text-muted">{row.k}</dt>
              <dd className="max-w-[260px] truncate text-right text-[12.5px] font-semibold text-ink">
                {row.node ?? row.v}
              </dd>
            </div>
          ))}
        </dl>
        <button
          type="button"
          onClick={onConfirm}
          aria-label="Use passkey to confirm this operation"
          className="group mx-auto mt-6 flex w-fit flex-col items-center gap-3 rounded-[14px] border border-line px-6 py-4 transition-colors hover:border-success hover:bg-success-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft"
        >
          <span aria-hidden className="grid size-14 place-items-center rounded-[14px] bg-success/90 text-2xl">🔑</span>
          <span className="text-[13px] text-muted group-hover:text-ink">Use your passkey to confirm this operation</span>
        </button>
      </div>
    </DialogShell>
  );
}

export default function ApprovalDetailFlow({ detail, initialStep, status }: {
  detail: ApprovalDetail;
  initialStep: ApprovalFlowStep;
  status: ApprovalStatus;
}) {
  const navigate = useNavigate();
  const approveTransfer = useTransferStore((s) => s.approveTransfer);
  const canApprove = usePermission("approval:approve");
  const canReject = usePermission("approval:reject");
  const canHold = usePermission("approval:hold");
  const canEscalate = usePermission("approval:escalate");
  const [step, setStep] = React.useState<ApprovalFlowStep>(status === "approved" ? "approved" : initialStep);
  const isApproved = step === "approved" || status === "approved";
  const isResolved = status !== "pending";
  const riskVariant = detail.risk === "high" ? "warning" : "success";
  const riskLabel = detail.risk === "high" ? "High Value" : "Low Risk";

  const confirmApproval = () => setStep("mfa");
  const confirmPasskey = () => {
    approveTransfer(detail.transferId);
    setStep("approved");
    toast({
      variant: "success",
      title: `${detail.approvalId} approved`,
      description: localOnly(`${detail.transferId} · ${detail.amount} will proceed to signing and broadcast.`),
    });
  };
  const toEscalate = (tab: "reject" | "hold" | "escalate") => navigate(`/approvals/${detail.id}/escalate?tab=${tab}`);

  return (
    <>
      <Breadcrumb items={[{ label: "Approvals", href: "/approvals" }, { label: detail.approvalId }]} />
      <div className="max-w-[720px]">
        <h1 className="text-xl font-bold text-ink">
          {detail.approvalId} — Transfer {detail.transferId}
        </h1>
        <p className="mt-1 text-[12.5px] text-muted">{detail.quorumLabel}</p>

        {isApproved ? (
          <InfoBanner variant="success" title="Approval complete" className="mt-4">
            {detail.approvalId} approved — {detail.amount}. The transfer will proceed to signing and broadcast.
          </InfoBanner>
        ) : null}
        {status === "rejected" && (
          <InfoBanner variant="danger" title="Approval rejected" className="mt-4">
            {detail.approvalId} was rejected. This transfer is terminal — see the transfer detail for the recorded reason.
          </InfoBanner>
        )}
        {status === "escalated" && (
          <InfoBanner variant="warning" title="Escalated to a senior approver" className="mt-4">
            {detail.approvalId} was escalated and reassigned. No further action is available here.
          </InfoBanner>
        )}

        <div className="mt-4 rounded-[10px] border border-line bg-surface px-5 py-2">
          {[
            { k: "Approval ID", v: detail.approvalId, strong: true, blue: false },
            {
              k: "Transfer",
              v: (
                <Link to={`/transfers/${detail.transferId}`} className="font-semibold text-primary hover:underline">
                  {detail.transferId}
                </Link>
              ),
              strong: false,
              blue: false,
            },
            { k: "Amount", v: detail.amount, strong: true, blue: false, big: true },
            { k: "Source", v: <WalletRefText text={detail.source} />, strong: false, blue: false },
            { k: "Destination", v: <WalletRefText text={detail.destination} />, strong: false, blue: false },
            { k: "Requester", v: detail.requester, strong: true, blue: false },
            { k: "Memo", v: detail.memo, strong: false, blue: false },
          ].map((row) => (
            <div key={row.k} className="flex items-baseline justify-between gap-4 py-2">
              <span className="w-[120px] shrink-0 text-[12px] text-muted">{row.k}</span>
              <span
                className={cn(
                  "text-right",
                  row.blue && "font-semibold text-primary",
                  row.strong && !row.blue && "font-medium text-ink",
                  !row.strong && !row.blue && "text-ink",
                  row.big && "text-[15px] font-bold",
                  !row.big && "text-[12.5px]"
                )}
>
                  {row.v}
                </span>
              </div>
            ))}
          <div className="flex items-baseline justify-between gap-4 py-2">
            <span className="w-[120px] shrink-0 text-[12px] text-muted">Risk Level</span>
            <Badge variant={riskVariant} dot={false} size="sm">
              {riskLabel}
            </Badge>
          </div>
        </div>

        <h2 className="mt-6 text-[15px] font-bold text-ink">Policy Triggers</h2>
        <div className="mt-2 rounded-lg border border-success-border bg-success-soft px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-medium text-muted">Policy Trigger</div>
              <div className="mt-0.5 text-[13px] font-bold text-primary">
                {(() => {
                  const m = detail.policyTitle.match(/^(POL-\d+)/);
                  return m ? (
                    <Link to={`/governance/${m[1]}`} className="hover:underline">
                      {detail.policyTitle}
                    </Link>
                  ) : (
                    detail.policyTitle
                  );
                })()}
              </div>
            </div>
            <span className="text-right text-[11px] font-bold text-success">✓ 1 threshold matched</span>
          </div>
          <p className="mt-2 max-w-[420px] text-[11.5px] leading-relaxed text-text">{detail.policyDesc}</p>
        </div>

        <h2 className="mt-4 text-[15px] font-bold text-ink">Screening</h2>
        <div className="mt-2 rounded-lg border border-success-border bg-success-soft px-4 py-3">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-medium text-muted">Screening Result</div>
              <div className="mt-0.5 text-[13px] font-bold text-primary">{detail.screeningTitle}</div>
            </div>
            <span className="text-right text-[11px] font-bold text-success">✓ No issues</span>
          </div>
          <p className="mt-2 max-w-[420px] text-[11.5px] leading-relaxed text-text">{detail.screeningDesc}</p>
        </div>

        <h2 className="mt-4 text-[15px] font-bold text-ink">Quorum</h2>
        <p className="mt-1 text-xs text-muted">{detail.quorumLabel}</p>
        <div className="mt-2 flex h-2 w-[300px] max-w-full overflow-hidden rounded-[4px] bg-disabled-soft">
          <div aria-hidden className="h-full bg-warning" style={{ width: `${detail.quorumPct}%` }} />
        </div>
        <div className="mt-2 flex flex-col gap-2">
          {detail.approvers.map((a) => (
            <div
              key={a.name}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3.5 py-2.5",
                a.status === "action" ? "border-2 border-primary bg-info-soft" : "border border-line bg-surface"
              )}
            >
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary text-[12px] font-bold text-on-primary">
                {a.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold leading-tight text-ink">{a.name}</div>
                <div className="mt-0.5 text-[11px] text-muted">{a.role}</div>
              </div>
              <Badge variant={a.status === "action" ? "warning" : "neutral"} dot={false} size="sm">
                {a.status === "action" ? "Action Required" : "Available"}
              </Badge>
            </div>
          ))}
        </div>

        {isApproved || isResolved ? (
          <div className="mt-6">
            <Button variant="primary" onClick={() => navigate("/approvals")}>
              Back to Approvals
            </Button>
          </div>
        ) : (
          <div className="mt-6 flex flex-wrap gap-2">
            {canApprove && (
              <Button variant="primary" onClick={() => setStep("confirm")}>
                Approve
              </Button>
            )}
            {canReject && (
              <Button variant="destructive" onClick={() => toEscalate("reject")}>
                Reject
              </Button>
            )}
            {canHold && (
              <Button variant="secondary" onClick={() => toEscalate("hold")}>
                Hold for Review
              </Button>
            )}
            {canEscalate && (
              <Button variant="secondary" onClick={() => toEscalate("escalate")}>
                Escalate
              </Button>
            )}
            {!canApprove && !canReject && !canHold && !canEscalate && (
              <p className="text-sm text-muted">You do not have permission to act on this approval.</p>
            )}
          </div>
        )}
      </div>

      {step === "confirm" && (
        <ConfirmApprovalDialog detail={detail} onConfirm={confirmApproval} onClose={() => setStep("idle")} />
      )}
      {step === "mfa" && <StepUpMfaDialog detail={detail} onConfirm={confirmPasskey} onClose={() => setStep("idle")} />}
    </>
  );
}